import {
  DateString,
  DateTimeString,
  RolodexEntryJson,
  RolodexEntryType,
} from 'types'
import { parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

export enum GeckoInspectionFormType {
  RUG_INSPECTION_FORM = 'rug_inspection_form',
  JSA_FORM = 'jsa_form',
  JLG_FORM = 'jlg_form',
}

export interface CalibrationStep {
  stepThickness: number
  unit: string
}

export interface InspectionResolution {
  height: number
  width: number
  widthUnit: string
  heightUnit: string
}

export interface RUGInspectionFormData {
  procedure: string
  coverageScope: string
  inspectionStartDate?: DateString
  inspectionEndDate?: DateString
  inspectionResolution: InspectionResolution
  deliverableResolution: InspectionResolution
  personnel: {
    fieldTechnicianName: string
    fieldTechnicianCertification: string
    projectManagerName: string
  }
  inspectionSurface: string
  surfaceCondition: string
  material?: string
  isCoated?: string
  surfaceTemperature?: number
  xAxisStartLocation?: string
  yAxisStartLocation?: string
  calibrationInfo: {
    calibrationBlockType: string
    calibrationBlockSerialNumber: string
    calibrationBlockMaterial: string
    thinStep: CalibrationStep
    thickStep: CalibrationStep
    verificationStep: CalibrationStep
    calibrationTimes: DateTimeString[]
  }
  robotInfo: {
    robotType: string
    robotSerialNumbers: string[]
    payloadCount: number
    payloadType: string
  }
  notes: string
}

export interface HazardItem {
  hazardType: string
  description: string
  riskRank: string
  correctiveActions: string
  correctiveActionsRiskRank: string
}

export interface JSAInspectionFormData extends Record<string, unknown> {
  // Header Section
  customer: string
  location: string
  jobNumber: string
  date: string
  workScope: string
  jointJobsiteVisitRequired?: string

  // Emergency Information
  fireExtinguisherLocation: string
  musterPoint: string
  eyeWashShowerLocation: string
  windDirectionWeather: string
  environmentalNotes?: string
  emergencyPhone?: string
  radioCH?: string
  emergencyPhoneLocation?: string
  siteSpecificOther?: string
  holeOrBottleWatchOther?: string

  // Equipment Section
  equipmentUsed?: string[]

  // PPE Section
  ppeRequired?: string[]

  // Permits Section
  permitsRequired?: string[]

  // Hazards Section
  hazardsIdentified?: string[]
  hazards?: HazardItem[]

  // Elevated Work Section
  elevatedWorkRequired?: string
  elevatedWorkType?: string[]
  potentialToFallFourFeet?: string
  fallArrestSystem?: string[]
  fallArrestSystemOther?: string
  elevatedWorkOther?: string
  customerFallRescuePlan?: string
  scaffoldingInspected?: string
  hoistingEquipmentRequired?: string
  reviewedRaisingLoweringEquipment?: string
  restrictedAccessZone?: string

  // Before Tasks Checklist
  beforeTasksHazards?: string[]
  otherBeforeTasksHazards?: string

  // Hazard Assessment Fields - dynamically typed from hazardAssessmentConfig.ts
  // NOTE: Individual hazard fields are not explicitly typed here to avoid duplication
  // The form uses Record<string, unknown> for dynamic hazard fields
  // Field names are generated from HAZARD_ASSESSMENT_CONFIGS

  // Task Workflow
  taskWorkflowStepCount?: number
  taskStep1?: string
  taskStep2?: string
  taskStep3?: string
  taskStep4?: string
  taskStep5?: string
  taskStep6?: string
  taskStep7?: string
  taskStep8?: string
  taskStep9?: string
  taskStep10?: string

  // JLG Daily Inspection Checklist
  jlgInspectedBy?: string
  jlgInspectionDate?: string
  jlgMake?: string
  jlgModel?: string
  jlgSerialNumber?: string
  jlgLocationWhereUsed?: string
  jlgTypeOfWork?: string
  jlgOperatingControls?: string
  jlgEmergencyControls?: string
  jlgAuxiliaryControls?: string
  jlgSafetyDevices?: string
  jlgPersonalProtectiveDevices?: string
  jlgPneumaticHydraulicFuelSystem?: string
  jlgCables?: string
  jlgWiringHarness?: string
  jlgLooseMissingParts?: string
  jlgTiresWheels?: string
  jlgPlacardsWarnings?: string
  jlgOperationsManual?: string
  jlgOutriggersStabilizers?: string
  jlgGuardrailSystem?: string
  jlgAdditionalComments?: string

  // Vehicle Inspection
  vehicleWalkAroundPerformed?: boolean | string
  vehicleCondition?: string
  fireExtinguisherInspected?: string
  damageNotedInSamsara?: string
  unitNumber?: string
  inspectedQuarterlyTag?: boolean | string
  worksAsIntended?: boolean | string

  // Final Assessment
  hazardsReducedToMinimum?: boolean | string
  canTaskBePerformedSafely?: boolean | string
  ifNoExplainWhy?: string
  tailgateMeetingMinutes?: string

  // LOTO Section
  lotoRequired?: string
  personalTeamLockApplied?: string
  lotoNotAppliedReason?: string
  lotoLockLocation?: string

  // Personnel
  geckoSupervisor?: string
  supervisorSignature?: string
  projectManager?: string
  radioChannel?: string
}

export interface JLGInspectionFormData {
  // Header Information
  inspectedBy: string
  inspectionDate: string

  // JLG Information
  make: string
  model: string
  serialNumber: string
  locationWhereUsed: string
  typeOfWork: string

  // Pre-Start Inspection Checklist
  operatingControls: string
  emergencyControls: string
  auxiliaryControls: string
  safetyDevices: string
  personalProtectiveDevices: string
  pneumaticHydraulicFuelSystem: string
  cables: string
  wiringHarness: string
  looseMissingParts: string
  tiresWheels: string
  placardsWarnings: string
  operationsManual: string
  outriggersStabilizers: string
  guardrailSystem: string
  additionalComments?: string
}

/**
 * The content of an inspection form
 * Determined by form type, can be unioned to include more forms in the future
 */
export type GeckoInspectionFormContent =
  | RUGInspectionFormData
  | JSAInspectionFormData
  | JLGInspectionFormData

export type GeckoInspectionFormData = {
  form_type: GeckoInspectionFormType
  date: DateString
  content: GeckoInspectionFormContent
}

/**
 * An entry which represents an inspection form for a job component inspection.
 */
export class GeckoInspectionForm extends GeckoEntryBase<
  GeckoInspectionFormData,
  {
    organization: string
    site: string
    unit: string
    [RolodexEntryType.COMPONENT]: string
    [RolodexEntryType.INSPECTION]: string
    [RolodexEntryType.JOB]: string
  }
> {
  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        form_type: json.data.form_type as GeckoInspectionFormType,
        date: json.data.date as DateString,
        content: json.data.content as GeckoInspectionFormContent,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        unit: parseString(json.tags.unit) ?? '',
        [RolodexEntryType.COMPONENT]:
          parseString(json.tags[RolodexEntryType.COMPONENT]) ?? '',
        [RolodexEntryType.INSPECTION]:
          parseString(json.tags[RolodexEntryType.INSPECTION]) ?? '',
        [RolodexEntryType.JOB]:
          parseString(json.tags[RolodexEntryType.JOB]) ?? '',
      },
      {}
    )
  }

  /**
   * A simple factory method which creates an inspection form from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoInspectionForm(json)
  }
}
